import { useForm, getFormProps } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { data, type ActionFunctionArgs, redirect } from '@remix-run/node'
import { useFetcher, useFetchers } from '@remix-run/react'
import { useState } from 'react'
import { ServerOnly } from 'remix-utils/server-only'
import { z } from 'zod'
import { Button } from '#app/components/ui/button.js'
import { Icon } from '#app/components/ui/icon.tsx'
import { Tabs, TabsList, TabsTrigger } from '#app/components/ui/tabs.js'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.js'
import { useHints, useOptionalHints } from '#app/utils/client-hints.tsx'
import { respondWithEmptyResponse } from '#app/utils/request-helpers.tsx'
import {
	useOptionalRequestInfo,
	useRequestInfo,
} from '#app/utils/request-info.ts'

import { type ObjectValues } from '#app/utils/typescript-helpers.js'

export const THEME_TYPE = {
	SYSTEM: 'system',
	LIGHT: 'light',
	DARK: 'dark',
} as const
export type IThemeType = ObjectValues<typeof THEME_TYPE>

export const ThemeTypeSchema = z.nativeEnum(THEME_TYPE)

const ThemeFormSchema = z.object({
	theme: ThemeTypeSchema,
	// this is useful for progressive enhancement
	redirectTo: z.string().optional(),
})

export async function loader() {
	return respondWithEmptyResponse()
}

export async function action({ request }: ActionFunctionArgs) {
	const { setTheme } = await import('#app/utils/theme.server.ts')
	
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid theme received')

	const { theme, redirectTo } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}
	if (redirectTo) {
		return redirect(redirectTo, responseInit)
	} else {
		return data({ result: submission.reply() }, responseInit)
	}
}

export function ThemeSwitch() {
	const fetcher = useFetcher<typeof action>()
	const requestInfo = useRequestInfo()
	const userPreference = requestInfo.userPrefs.theme

	const [form] = useForm({
		id: 'theme-switch',
		lastResult: fetcher.data?.result,
	})

	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? userPreference ?? 'system'
	const nextMode =
		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
	const modeLabel = {
		light: (
			<Icon name="sun">
				<span className="sr-only">Light</span>
			</Icon>
		),
		dark: (
			<Icon name="moon">
				<span className="sr-only">Dark</span>
			</Icon>
		),
		system: (
			<Icon name="laptop">
				<span className="sr-only">System</span>
			</Icon>
		),
	}

	return (
		<fetcher.Form
			method="POST"
			{...getFormProps(form)}
			action="/resources/theme-switch"
		>
			<ServerOnly>
				{() => (
					<input type="hidden" name="redirectTo" value={requestInfo.path} />
				)}
			</ServerOnly>
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex gap-2">
				<Button type="submit" variant="none" size="icon" textSize="base">
					{modeLabel[mode as keyof typeof modeLabel]}
				</Button>
			</div>
		</fetcher.Form>
	)
}

export function ThemeTabs() {
	const fetcher = useFetcher<typeof action>()

	const requestInfo = useRequestInfo()
	const userPreference = requestInfo.userPrefs.theme

	const [form] = useForm({
		id: 'theme-tabs',
		lastResult: fetcher.data?.result,
	})

	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? userPreference ?? 'system'
	const [theme, setTheme] = useState<IThemeType>(mode)

	const themeOptions = [
		{
			value: 'light',
			icon: 'sun',
			label: 'Light mode',
		},
		{
			value: 'dark',
			icon: 'moon',
			label: 'Dark mode',
		},
		{
			value: 'system',
			icon: 'laptop',
			label: 'System mode',
		},
	] as const

	return (
		<fetcher.Form
			method="POST"
			{...getFormProps(form)}
			action="/resources/theme-switch"
		>
			<ServerOnly>
				{() => (
					<input type="hidden" name="redirectTo" value={requestInfo.path} />
				)}
			</ServerOnly>
			<TooltipProvider delayDuration={0}>
				<Tabs
					defaultValue={theme}
					onValueChange={(value) => {
						setTheme(value as IThemeType)
						fetcher.submit(
							{ theme: value },
							{ method: 'POST', action: '/resources/theme-switch' },
						)
					}}
				>
					<TabsList className="flex gap-1">
						{themeOptions.map((option) => {
							const currentTheme = theme === option.value
							return (
								<Tooltip key={option.value}>
									<TooltipTrigger
										data-state={currentTheme ? 'active' : 'inactive'}
										asChild
									>
										<TabsTrigger
											value={option.value}
											aria-label={option.label}
											className="px-2"
										>
											<div className="flex md:hidden">
												<Icon size="md" name={option.icon}>
													<span className="sr-only">{option.value}</span>
												</Icon>
											</div>
											<div className="hidden md:flex">
												<Icon size="sm" name={option.icon}>
													<span className="sr-only">{option.value}</span>
												</Icon>
											</div>
										</TabsTrigger>
									</TooltipTrigger>
									<TooltipContent className="capitalize">
										{option.value}
									</TooltipContent>
								</Tooltip>
							)
						})}
					</TabsList>
				</Tabs>
			</TooltipProvider>
		</fetcher.Form>
	)
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(
		(f) => f.formAction === '/resources/theme-switch',
	)

	if (themeFetcher && themeFetcher.formData) {
		const submission = parseWithZod(themeFetcher.formData, {
			schema: ThemeFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.theme
		}
	}
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system' ? hints.theme : optimisticMode
	}
	return requestInfo.userPrefs.theme ?? hints.theme
}

export function useOptionalTheme() {
	const optionalHints = useOptionalHints()
	const optionalRequestInfo = useOptionalRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system' ? optionalHints?.theme : optimisticMode
	}
	return optionalRequestInfo?.userPrefs.theme ?? optionalHints?.theme
}
